/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ScorecardIcon from '@atlaskit/icon/core/scorecard';
import IconTile from '@atlaskit/icon/icon-tile';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getAppearanceFromId } from '@atlassian/conversation-assistant-utils/icon-tile-utils';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';

import messages from './messages';

// ── Layout ──────────────────────────────────────────────────────────────

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxShadow: token('elevation.shadow.overflow'),
		overflow: 'clip',
		width: '100%',
		backgroundColor: token('elevation.surface'),
	},
	header: {
		display: 'flex',
		gap: token('space.150'),
		alignItems: 'center',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	titleArea: {
		display: 'flex',
		flex: '1 0 0',
		flexDirection: 'column',
		gap: token('space.050'),
		justifyContent: 'center',
		minWidth: '0px',
	},
});

// ── Main component ──────────────────────────────────────────────────────

export type TaskCompletedViewProps = {
	title?: string;
	onDoneClicked?: () => void;
};

export const TaskCompletedView = ({
	title,
	onDoneClicked,
}: TaskCompletedViewProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.container}>
			<div css={styles.header}>
				<IconTile
					icon={ScorecardIcon}
					label=""
					appearance={getAppearanceFromId(title || formatMessage(messages.defaultTitle))}
					size="medium"
				/>
				<div css={styles.titleArea}>
					<Inline alignBlock="center" space="space.050">
						<Text weight="semibold" maxLines={1} as="span">
							{title || formatMessage(messages.defaultTitle)}
						</Text>
					</Inline>
					<RainbowProgressBar status="complete" label={formatMessage(messages.progressBarLabel)} />
				</div>
				<Button appearance="default" onClick={onDoneClicked}>
					{formatMessage(messages.doneButtonLabel)}
				</Button>
			</div>
		</div>
	);
};
